// @flow
import React, { PureComponent } from 'react';
import DidItRecommend from 'app/common/react/components/do/aggregated-pins/did-it/DidItRecommend/DidItRecommend';
import FacebookInit from 'app/common/react/components/do/aggregated-pins/did-it/DidItRecommend/FacebookInit';
import FbUtil from 'app/common/lib/FbUtil';
import i18n from 'app/common/lib/i18n';
import { hasPin } from 'app/common/react/components/closeup/utils';
import { Spinner } from 'app/common/react/components/do/aggregated-pins/did-it/DidItRecommend/common';
import { track } from 'app/common/lib/react/analytics';
import withResource, { type Resource } from 'app/common/lib/react/withResource';
import {
  toMaybeHasRichTitle as hasRichTitle,
  toMaybeHasRichMetadata as hasRichMetadata,
} from 'app/common/react/components/closeup/lib';
import { Box, Icon, Text } from 'gestalt';

const FB_PUBLISH_ACTIONS_PERMISSION = 'publish_actions';
const FB_SHARE_BUTTON_BG_COLOR_CHECKED = '#e60023';
const FB_SHARE_BUTTON_BG_COLOR_UNCHECKED = '#ffffff';

type Props = {|
  aggregatedPinDataId: string,
  isDoneByMe: boolean,
  loggedInUser: {
    username: string,
    id: string,
  },
  onDoneStateChanged?: (stateChanged: boolean) => void, // indicates if isDoneByMe has changed
  pinId: string,
  pinResource: Resource<{ rich_metadata?: { title: ?string }, id: string }>,
  pinTitle?: string,
  userDidItDataId?: string,
|};

type State = {|
  shareToFacebookCheckedOn: boolean,
  shareToFacebookDisabled: boolean,
  shareToFacebookPermissionGranted: boolean,
|};

export class DidItModal extends PureComponent<Props, State> {
  state = {
    shareToFacebookCheckedOn: false,
    shareToFacebookDisabled: false,
    shareToFacebookPermissionGranted: false,
  };

  handleToggleFacebookShare = () => {
    const {
      pinResource: { data },
    } = this.props;
    const { shareToFacebookCheckedOn } = this.state;

    // Reassigning so that in the return if - we can check for positive instead of negative.
    const share = !shareToFacebookCheckedOn;
    const pinId = (data && data.id) || '';

    if (share) {
      window.FB.login(
        (response) => {
          if (response.authResponse) {
            FbUtil.checkFbTimelinePermission({
              success: () => {
                track(PCONST.thrift.EventType.CLICK, {
                  view: PCONST.thrift.ViewType.PIN_DID_IT,
                  component: PCONST.thrift.ComponentType.DID_IT_MODAL_FULL_SHEET,
                  element: PCONST.thrift.ElementType.DID_IT_FACEBOOK_SHARE_TOGGLE,
                  objectId: pinId,
                });
                this.setState({
                  shareToFacebookCheckedOn: true,
                  shareToFacebookPermissionGranted: true,
                });
              },
              error: () => {
                this.setState({
                  shareToFacebookCheckedOn: false,
                  shareToFacebookPermissionGranted: false,
                  shareToFacebookDisabled: true,
                });
              },
            });
          } else {
            this.setState({
              shareToFacebookCheckedOn: false,
              shareToFacebookPermissionGranted: false,
              shareToFacebookDisabled: true,
            });
          }
        },
        { scope: FB_PUBLISH_ACTIONS_PERMISSION },
      );
    } else {
      track(PCONST.thrift.EventType.CLICK, {
        view: PCONST.thrift.ViewType.PIN_DID_IT,
        component: PCONST.thrift.ComponentType.DID_IT_MODAL_FULL_SHEET,
        element: PCONST.thrift.ElementType.DID_IT_FACEBOOK_SHARE_TOGGLE,
        objectId: pinId,
      });
      this.setState({ shareToFacebookCheckedOn: false });
    }
  };

  render() {
    const {
      aggregatedPinDataId,
      isDoneByMe,
      loggedInUser,
      onDoneStateChanged,
      pinId,
      pinResource,
      pinTitle,
      userDidItDataId,
    } = this.props;
    const { shareToFacebookCheckedOn, shareToFacebookPermissionGranted } = this.state;

    if (!pinResource.isLoaded) {
      return <Spinner />;
    }

    const { title } = hasRichTitle(hasRichMetadata(hasPin({ pin: pinResource.data }))) || {};
    const fbShareButtonBgColor = shareToFacebookCheckedOn
      ? FB_SHARE_BUTTON_BG_COLOR_CHECKED
      : FB_SHARE_BUTTON_BG_COLOR_UNCHECKED;

    return (
      <Box color="white" shape="rounded" display="flex">
        <DidItRecommend
          aggregatedPinDataId={aggregatedPinDataId}
          isDoneByMe={isDoneByMe}
          loggedInUser={loggedInUser}
          onDoneStateChanged={onDoneStateChanged}
          pinId={pinId}
          pinTitle={pinTitle || title}
          shareToFacebookCheckedOn={shareToFacebookCheckedOn}
          shareToFacebookPermissionGranted={shareToFacebookPermissionGranted}
          userDidItDataId={userDidItDataId}
        />
        {!isDoneByMe && (
          <Box
            display="flex"
            position="absolute"
            justifyContent="around"
            alignItems="center"
            deprecatedMargin={{ left: 4, bottom: 5 }}
            bottom
            left
          >
            <Box xs={{ column: 1 }}>
              <button
                className="didItFacebookShareButton"
                aria-label={i18n._('Share on Facebook', 'Share tried-it on Facebook')}
                onClick={this.handleToggleFacebookShare}
                style={{
                  padding: shareToFacebookCheckedOn ? '4px' : '8px',
                  borderRadius: '50%',
                  border: shareToFacebookCheckedOn ? '0' : '2px solid #8e8e8e',
                  backgroundColor: fbShareButtonBgColor,
                  outline: 'none',
                }}
              >
                {shareToFacebookCheckedOn && (
                  <Icon
                    accessibilityLabel={i18n._('Share on Facebook', 'Share tried-it on Facebook')}
                    size={12}
                    color="white"
                    icon={'check'}
                  />
                )}
                <FacebookInit />
              </button>
            </Box>
            <Box deprecatedPadding={{ x: 1 }}>
              <Text lgSize="sm" mdSize="xs" size="sm" smSize="xs" weight="bold">
                {i18n._('Share on Facebook', 'Share tried-it on Facebook')}
              </Text>
            </Box>
          </Box>
        )}
      </Box>
    );
  }
}

export default withResource({
  name: 'PinResource',
  key: 'pinResource',
  options: ({ pinId }: { pinId: string }) => ({
    field_set_key: 'detailed',
    id: pinId,
  }),
})(DidItModal);
